import React from "react";
import { DatePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";

type DateSelectorProps = {
  label?: string;
  required?: boolean;
  name?: string;
  /** Accepts a Date, a `YYYY-MM-DD`/ISO string, or null. */
  value?: Date | string | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  error?: string;
  /** When true, every date before today is disabled (good for expiry / future-only fields). */
  disablePast?: boolean;
  /** When true, every date after today is disabled (good for "created" filters). */
  disableFuture?: boolean;
  className?: string;
};

const startOfToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfToday = (): Date => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const coerceDate = (raw: Date | string | null | undefined): Date | null => {
  if (raw == null) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const DateSelector: React.FC<DateSelectorProps> = ({
  label,
  required,
  name,
  value,
  onChange,
  placeholder,
  error,
  disablePast,
  disableFuture,
  className = "",
}) => {
  const safeValue = coerceDate(value);

  const shouldDisableDate = (date: Date): boolean => {
    if (disablePast && date < startOfToday()) return true;
    if (disableFuture && date > endOfToday()) return true;
    return false;
  };

  return (
    <div className={`mb-4 mt-2 w-full ${className}`}>
      {label && (
        <label className="block font-medium mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <DatePicker
        name={name}
        value={safeValue}
        onChange={onChange}
        placeholder={placeholder ?? "Select a date"}
        format="yyyy-MM-dd"
        block
        cleanable
        container={() => document.body}
        shouldDisableDate={
          disablePast || disableFuture ? shouldDisableDate : undefined
        }
      />
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default DateSelector;
